import { useMemo, useState, useRef, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queries } from '@/data';
import type { ComuneFull } from '@/data';
import { useVisitPlannerStore } from './store';
import {
  planTrip,
  googleMapsUrl,
  googleMapsNavUrl,
  appleMapsNavUrl,
  isAppleDevice,
  haversineKm,
  formatDuration,
  formatDistance,
  type OsrmStop,
  type OsrmTrip,
} from './osrm';
import { downloadGpx } from './gpx';
import { isPremium, unlockPremium } from './premium';

// ── Visited state (per trip, persisted in localStorage) ─────────────────────

function tripKey(istats: string[]): string {
  return [...istats].sort().join(',');
}

function loadVisited(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(`rm:visited:${key}:v1`);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveVisited(key: string, ids: Set<string>): void {
  try {
    localStorage.setItem(`rm:visited:${key}:v1`, JSON.stringify([...ids]));
  } catch {
    // localStorage may be unavailable (quota / private mode). Best-effort.
  }
}

// ── Main component ───────────────────────────────────────────────────────────

interface VisitPlannerDialogProps {
  /**
   * ISO-3166 alpha-2 (upper-case) of the map this dialog belongs to — the
   * route's country, handed down by MapPage. Required on purpose: the unlock
   * capture is filed under it, and a default would quietly re-create the bug
   * it replaces (every address stored as Italy, whatever map you were on).
   */
  countryCode: string;
}

export function VisitPlannerDialog({ countryCode }: VisitPlannerDialogProps) {
  const { t } = useTranslation();
  const istats = useVisitPlannerStore((s) => s.openFor);
  const close = useVisitPlannerStore((s) => s.close);

  const key = useMemo(() => (istats ? tripKey(istats) : ''), [istats]);
  const [visited, setVisited] = useState<Set<string>>(() => (istats ? loadVisited(tripKey(istats)) : new Set()));
  const [nearestIstat, setNearestIstat] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [premium, setPremium] = useState(() => isPremium());
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockFailed, setUnlockFailed] = useState(false);
  const unlockRef = useRef<HTMLInputElement>(null);
  const onApple = isAppleDevice();

  // Re-load visited state whenever the trip identity changes (different shortlist
  // → different localStorage namespace). Without this the dialog keeps the
  // previous trip's checkmarks when a new shortlist is opened. The key-change
  // pattern below avoids the React-19 "setState in effect" anti-pattern by
  // doing the reset during render (single render, no cascading effect).
  const [prevKey, setPrevKey] = useState(key);
  if (prevKey !== key) {
    setPrevKey(key);
    setVisited(istats ? loadVisited(tripKey(istats)) : new Set());
    setNearestIstat(null);
  }

  const { data: meta } = useQuery(queries.meta());
  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(meta?.version ?? ''),
    enabled: !!meta?.version && !!istats,
  });

  const stops = useMemo<OsrmStop[]>(() => {
    if (!istats || !fullIndex) return [];
    return istats
      .map((istat): OsrmStop | null => {
        const c: ComuneFull | undefined = fullIndex.byIstat.get(istat);
        if (!c) return null;
        return { istat: c.istat, name: c.name, lat: c.lat, lon: c.lon };
      })
      .filter((s): s is OsrmStop => s !== null);
  }, [istats, fullIndex]);

  const trip = useQuery({
    queryKey: ['osrm-trip', stops.map((s) => s.istat).sort().join(',')],
    queryFn: () => planTrip(stops),
    enabled: stops.length >= 2 && stops.length <= 7,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  if (!istats) return null;

  const tooFew = stops.length < 2;
  const tooMany = stops.length > 7;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const toggleVisited = (istat: string) => {
    const next = new Set(visited);
    if (next.has(istat)) next.delete(istat);
    else next.add(istat);
    setVisited(next);
    saveVisited(key, next);
  };

  const handleImHere = () => {
    if (!premium) {
      setShowUnlock(true);
      setTimeout(() => unlockRef.current?.focus(), 50);
      return;
    }
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        if (!trip.data) return;
        const nearest = trip.data.ordered.reduce<{ istat: string; dist: number } | null>(
          (best, s) => {
            const d = haversineKm(pos.coords.latitude, pos.coords.longitude, s.lat, s.lon);
            if (!best || d < best.dist) return { istat: s.istat, dist: d };
            return best;
          },
          null,
        );
        setNearestIstat(nearest?.istat ?? null);
      },
      () => setLocating(false),
      { timeout: 10000, maximumAge: 30000 },
    );
  };

  const handleUnlock = async (e: FormEvent) => {
    e.preventDefault();
    if (!unlockEmail.includes('@') || unlocking) return;
    setUnlocking(true);
    setUnlockFailed(false);
    const ok = await unlockPremium(unlockEmail, countryCode);
    setUnlocking(false);
    if (!ok) {
      // No record, no link. Leave the panel and the typed address in place so
      // one click retries, rather than silently pretending it went through.
      setUnlockFailed(true);
      return;
    }
    setPremium(true);
    setShowUnlock(false);
    setUnlockEmail('');
    // Auto-trigger geolocation immediately after unlock
    setTimeout(() => handleImHere(), 100);
  };

  // ── Next unvisited stop for "I'm here" navigation ─────────────────────────
  const nextStop = trip.data
    ? (nearestIstat
        ? (() => {
            const idx = trip.data!.ordered.findIndex((s) => s.istat === nearestIstat);
            return trip.data!.ordered.slice(idx + 1).find((s) => !visited.has(s.istat)) ?? null;
          })()
        : trip.data.ordered.find((s) => !visited.has(s.istat)) ?? null)
    : null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={close}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="visit-planner-title"
        className="relative z-10 w-full max-w-lg overflow-hidden rounded-lg bg-[color:var(--color-paper)] shadow-2xl"
      >
        {/* Header */}
        <header className="border-b border-slate-200 px-5 py-3">
          <h2
            id="visit-planner-title"
            className="font-serif text-lg font-medium text-[color:var(--color-ink-900)]"
          >
            🗺 {t('visitPlanner.title', { defaultValue: 'Visit plan' })}
          </h2>
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-500)]">
            {t('visitPlanner.subtitle', {
              count: stops.length,
              defaultValue: 'Optimal driving order for {{count}} stops.',
            })}
          </p>
        </header>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {tooFew && (
            <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('visitPlanner.tooFew', { defaultValue: 'Select at least 2 comuni to plan a trip.' })}
            </div>
          )}
          {tooMany && (
            <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('visitPlanner.tooMany', {
                defaultValue: 'Up to 7 stops per trip (OSRM limit). You picked {{count}}.',
                count: stops.length,
              })}
            </div>
          )}
          {!tooFew && !tooMany && trip.isPending && (
            <div className="py-2 text-sm text-slate-500">
              {t('visitPlanner.computing', { defaultValue: 'Computing route…' })}
            </div>
          )}
          {!tooFew && !tooMany && trip.error && (
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {t('visitPlanner.error', {
                defaultValue: 'Could not compute route: {{msg}}',
                msg: (trip.error as Error).message,
              })}
            </div>
          )}

          {trip.data && (
            <>
              {/* Stop list */}
              <ol className="mb-3 space-y-1.5">
                {trip.data.ordered.map((s, i) => {
                  const isNearest = s.istat === nearestIstat;
                  const isDone = visited.has(s.istat);
                  return (
                    <li
                      key={s.istat}
                      className={`flex items-center gap-2 rounded px-1 py-0.5 text-sm transition-colors ${
                        isNearest ? 'bg-[color:var(--color-terra-50)] ring-1 ring-[color:var(--color-terra-200)]' : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleVisited(s.istat)}
                        aria-label={isDone ? t('visitPlanner.markUnvisited', { defaultValue: 'Mark as unvisited' }) : t('visitPlanner.markVisited', { defaultValue: 'Mark as visited' })}
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                          isDone
                            ? 'bg-[color:var(--color-moss-600)] text-white'
                            : 'bg-[color:var(--color-terra-600)] text-[color:var(--color-paper)]'
                        }`}
                      >
                        {isDone ? '✓' : i + 1}
                      </button>
                      <span className={isDone ? 'text-[color:var(--color-ink-400)] line-through' : 'text-[color:var(--color-ink-900)]'}>
                        {s.name}
                      </span>
                      {isNearest && (
                        <span className="ml-auto shrink-0 font-mono text-[9px] uppercase tracking-wider text-[color:var(--color-terra-600)]">
                          {t('visitPlanner.youAreHere', { defaultValue: 'Here' })}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>

              {/* Stats */}
              <StatsRow trip={trip.data} t={t} />

              {/* "I'm here" navigation — shown when active */}
              {nearestIstat && nextStop && (
                <div className="mb-3 rounded-lg border border-[color:var(--color-terra-200)] bg-[color:var(--color-terra-50)] p-3">
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-[color:var(--color-terra-700)]">
                    {t('visitPlanner.nextStop', { defaultValue: 'Next stop' })}
                  </p>
                  <p className="mb-2 font-medium text-[color:var(--color-ink-900)]">{nextStop.name}</p>
                  <div className="flex gap-2">
                    <a
                      href={googleMapsNavUrl(nextStop)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded bg-[color:var(--color-terra-600)] px-3 py-1.5 font-mono text-xs text-white hover:bg-[color:var(--color-terra-700)]"
                    >
                      Google Maps →
                    </a>
                    {onApple && (
                      <a
                        href={appleMapsNavUrl(nextStop)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded border border-[color:var(--color-terra-300)] px-3 py-1.5 font-mono text-xs text-[color:var(--color-terra-700)] hover:bg-[color:var(--color-terra-100)]"
                      >
                        Apple Maps →
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Premium unlock form */}
              {showUnlock && (
                <div className="mb-3 rounded-lg border border-[color:var(--color-ink-200)] bg-[color:var(--color-paper-2)] p-4">
                  <p className="mb-1 font-display text-sm italic text-[color:var(--color-ink-900)]">
                    {t('visitPlanner.unlockTitle', { defaultValue: 'Unlock "I\'m here" mode' })}
                  </p>
                  <p className="mb-2 font-mono text-[10px] text-[color:var(--color-ink-500)]">
                    {t('visitPlanner.unlockDesc', {
                      defaultValue: 'Geolocation + one-tap navigation to your next stop. Free — no credit card.',
                    })}
                  </p>
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-[color:var(--color-ink-400)]">
                    {t('visitPlanner.unlockBenefitsTitle', { defaultValue: 'What you unlock' })}
                  </p>
                  <ul className="mb-3 space-y-0.5 font-mono text-[11px] text-[color:var(--color-ink-700)]">
                    <li>· {t('visitPlanner.unlockBenefit1', { defaultValue: 'Live geolocation pin on the map' })}</li>
                    <li>· {t('visitPlanner.unlockBenefit2', { defaultValue: 'Auto-detected next nearest stop in your route' })}</li>
                    <li>· {t('visitPlanner.unlockBenefit3', { defaultValue: 'One-tap deep link into Apple/Google Maps' })}</li>
                    <li>· {t('visitPlanner.unlockBenefit4', { defaultValue: '"Visited" check-marks that sync across devices' })}</li>
                  </ul>
                  <p className="mb-3 font-mono text-[10px] italic text-[color:var(--color-ink-400)]">
                    {t('visitPlanner.unlockWhyEmail', {
                      defaultValue:
                        'Why we ask: a one-time link to re-open this mode on another device. No newsletter, no card — opt-out in every email.',
                    })}
                  </p>
                  <form onSubmit={handleUnlock} className="flex gap-2">
                    <input
                      ref={unlockRef}
                      type="email"
                      value={unlockEmail}
                      onChange={(e) => setUnlockEmail(e.target.value)}
                      placeholder={t('visitPlanner.unlockPlaceholder', { defaultValue: 'you@example.com' })}
                      required
                      className="min-w-0 flex-1 rounded border border-[color:var(--color-ink-200)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs text-[color:var(--color-ink-900)] placeholder-[color:var(--color-ink-400)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-terra-600)]"
                    />
                    <button
                      type="submit"
                      disabled={unlocking}
                      className="shrink-0 rounded bg-[color:var(--color-terra-600)] px-3 py-1.5 font-mono text-xs font-medium text-white hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
                    >
                      {unlocking
                        ? t('visitPlanner.unlockSending')
                        : t('visitPlanner.unlockBtn', { defaultValue: 'Unlock free' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUnlock(false)}
                      className="font-mono text-xs text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
                      aria-label="Cancel"
                    >
                      ✕
                    </button>
                  </form>
                  {unlockFailed && (
                    <p
                      className="mt-2 font-mono text-[11px] text-[color:var(--color-rust-600)]"
                      role="status"
                      data-testid="visit-planner-unlock-error"
                    >
                      {t('visitPlanner.unlockFailed')}
                    </p>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                <a
                  href={googleMapsUrl(trip.data)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md bg-[color:var(--color-terra-600)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)]"
                >
                  {t('visitPlanner.openGmaps', { defaultValue: 'Open in Google Maps' })}
                </a>
                <button
                  type="button"
                  onClick={() => downloadGpx(trip.data!)}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {t('visitPlanner.downloadGpx', { defaultValue: 'Download GPX' })}
                </button>
                {navigator.geolocation && (
                  <button
                    type="button"
                    onClick={handleImHere}
                    disabled={locating}
                    className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {locating
                      ? t('visitPlanner.locating', { defaultValue: '…' })
                      : t('visitPlanner.imHere', { defaultValue: "📍 I'm here" })}
                    {!premium && (
                      <span className="rounded bg-amber-100 px-1 font-mono text-[8px] font-semibold uppercase tracking-wider text-amber-700">
                        {t('visitPlanner.premiumBadge', { defaultValue: 'free' })}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="flex justify-end border-t border-slate-200 px-5 py-2">
          <button
            type="button"
            onClick={close}
            className="rounded-md px-3 py-1 text-sm text-slate-600 hover:bg-slate-100"
          >
            {t('common.close', { defaultValue: 'Close' })}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Stats sub-component ──────────────────────────────────────────────────────

function StatsRow({
  trip,
  t,
}: {
  trip: OsrmTrip;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="mb-4 flex gap-4 border-t border-slate-200 pt-3 text-sm">
      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {t('visitPlanner.drive', { defaultValue: 'Drive' })}
        </span>
        <span className="font-mono text-[color:var(--color-ink-900)]">
          {formatDuration(trip.totalDuration)}
        </span>
      </div>
      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {t('visitPlanner.distance', { defaultValue: 'Distance' })}
        </span>
        <span className="font-mono text-[color:var(--color-ink-900)]">
          {formatDistance(trip.totalDistance)}
        </span>
      </div>
      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {t('visitPlanner.stops', { defaultValue: 'Stops' })}
        </span>
        <span className="font-mono text-[color:var(--color-ink-900)]">{trip.ordered.length}</span>
      </div>
    </div>
  );
}
